import type { Schema, SuccessResponse } from "../types/api-helpers.d.ts";
import { $ } from "../lib/globals.js";
import { APP_CONFIG } from "../lib/config.js";
import { emit } from "../lib/metrics-client.js";
import { clearOpenForm } from "../lib/modal-tracking.js";
import { UI_EVENTS } from "../types/metrics-events.js";
import { showNewPageOnAJAXHTMLResponse } from "../lib/page-utils.js";
import {
  showSplashModalAlertBanner,
  resetModalFormState,
  handleImproperFormErrors,
  switchModal,
} from "./init.js";
import { VALIDATION_FORM } from "../types/metrics-dim-values.js";
import { debug } from "../lib/debug.js";

const log = debug("splash:register");

type RegisterRequest = Schema<"RegisterRequest">;
type RegisterSuccess = SuccessResponse<"registerUser", 200>;
type RegisterError = Schema<"ErrorResponse_RegisterErrorCodes">;

/**
 * Initialize register form handlers
 * Must be called after register form HTML is loaded into the modal
 */
export function initRegisterForm($modal: JQuery): void {
  $modal
    .find("#ToLoginFromRegister")
    .offAndOn("click", () => switchModal($modal, "#LoginModal"));

  $modal
    .find("#submit")
    .offAndOn("click", (event) => handleRegister(event, $modal));

  $modal.on("show.bs.modal", () => resetModalFormState($modal));

  log("initRegisterForm bound");
}

function handleRegister(event: JQuery.TriggeredEvent, $modal: JQuery): void {
  event.preventDefault();
  emit({ event: UI_EVENTS.UI_REGISTER_SUBMIT });
  clearOpenForm();
  $modal.find("#submit").attr("disabled", "disabled").attr("aria-busy", "true");

  const username: string = String($modal.find("#username").val() ?? "");
  const email: string = String($modal.find("#email").val() ?? "");
  const confirmEmail: string = String($modal.find("#confirmEmail").val() ?? "");
  const password: string = String($modal.find("#password").val() ?? "");
  const confirmPassword: string = String(
    $modal.find("#confirmPassword").val() ?? "",
  );

  log("handleRegister submit", {
    usernameLength: username.length,
    emailLength: email.length,
    confirmEmailLength: confirmEmail.length,
    passwordLength: password.length,
    confirmPasswordLength: confirmPassword.length,
    emailsMatch: email === confirmEmail,
    passwordsMatch: password === confirmPassword,
  });

  const payload: RegisterRequest = {
    username,
    email,
    confirmEmail,
    password,
    confirmPassword,
  };
  const registerRequest: JQuery.jqXHR = $.ajax({
    url: APP_CONFIG.routes.register,
    type: "POST",
    data: JSON.stringify(payload),
    contentType: "application/json",
  });

  registerRequest.done((response, _textStatus, xhr) =>
    handleRegisterSuccess({ response, xhr, $modal, email }),
  );
  registerRequest.fail((xhr, textStatus, error) =>
    handleRegisterFailure(xhr, textStatus, error, $modal),
  );
}

function handleRegisterSuccess({
  response,
  xhr,
  $modal,
  email,
}: {
  response: RegisterSuccess;
  xhr: JQuery.jqXHR;
  $modal: JQuery;
  email: string;
}): void {
  if (xhr.status !== 200) {
    return;
  }
  log("register acknowledged, opening confirmation modal");

  const $confirmationModal: JQuery = $("#RegisterConfirmationModal");
  // Stash the submitted email + the uniform confirmation message so the
  // confirmation modal's Resend handler can re-POST and re-announce them.
  $confirmationModal
    .data("registerEmail", email)
    .data("confirmMessage", response.message);

  // DD-23: announce on shown.bs.modal (AFTER the modal is visible) so the live
  // role="alert" region is rendered before its text mutates and AT announces it.
  $confirmationModal.one("shown.bs.modal", () =>
    showSplashModalAlertBanner($confirmationModal, response.message, "success"),
  );

  switchModal($modal, "#RegisterConfirmationModal");
}

function handleRegisterFailure(
  xhr: JQuery.jqXHR,
  _: string,
  __: string,
  $modal: JQuery,
): void {
  if (!("responseJSON" in xhr)) {
    log("register failure: non-JSON response", {
      status: xhr.status,
      contentType: xhr.getResponseHeader("Content-Type"),
    });
    if (xhr.getResponseHeader("Content-Type") === "text/html; charset=utf-8") {
      switch (xhr.status) {
        case 403:
        case 429: {
          showNewPageOnAJAXHTMLResponse(xhr.responseText);
          return;
        }
      }
    }
    window.location.assign(APP_CONFIG.routes.errorPage);
    return;
  }

  if ("errorCode" in xhr.responseJSON) {
    const errorJson = xhr.responseJSON as RegisterError;
    log("register failure: JSON error", {
      status: xhr.status,
      errorCode: errorJson.errorCode,
    });
    emit({
      event: UI_EVENTS.UI_VALIDATION_ERROR,
      form: VALIDATION_FORM.REGISTER,
    });
    switch (xhr.status) {
      case 400: {
        handleImproperFormErrors($modal, errorJson);
        $modal.find("#submit").removeAttr("disabled").removeAttr("aria-busy");
        break;
      }
    }
  } else {
    log("register failure: unhandled JSON shape", { status: xhr.status });
    showSplashModalAlertBanner(
      $modal,
      "Unable to process request...",
      "danger",
    );
    $modal.find("#submit").removeAttr("disabled").removeAttr("aria-busy");
  }
}
